/**
 * Sport adapter config for unified schedule results tab.
 * getScheduleResultsConfig(sportId) returns columns, relevantMarketId, eventColumnLabelKey, renderEventCell, renderMarketCell.
 */
import React from 'react';
import { vsMarketTypeKey, vsOutcomeTypeKey } from 'lib/virtualSportsI18n';
import {
  BASEBALL_RELEVANT_MARKET_ID,
  getRelevantMarketIdForRacing,
  getRelevantMarketIdForGeneric,
} from 'features/markets/components/bets-area/resultsMobileHelpers';
import { getMarketTypeIdsFromResults } from 'features/markets/components/bets-area/useScheduleResultsTab';
import RacingOutcomeCell from 'features/markets/components/bets-area/RacingOutcomeCell';
import BaseballOutcomeCell from 'features/markets/components/bets-area/BaseballOutcomeCell';

const RACING_SPORT_IDS = [10, 11, 20, 21, 30];
const BASEBALL_SPORT_ID = 40;
const BASEBALL_MARKET_IDS = [4001, 4002, 4003, 4004];

function isBaseball(sportId) {
  return sportId != null && Number(sportId) === BASEBALL_SPORT_ID;
}

function isRacing(sportId) {
  const id = sportId != null ? Number(sportId) : null;
  return id != null && RACING_SPORT_IDS.includes(id);
}

function getGenericOutcomeLabel(row, marketTypeId, t) {
  const raw = row[String(marketTypeId)] ?? row[marketTypeId];
  if (raw == null) return '—';
  const items = Array.isArray(raw) ? raw : [raw];
  return items
    .map((it) => {
      if (it != null && typeof it === 'object' && 'result' in it) return (it.result != null ? String(it.result) : '');
      return it != null ? String(it) : '';
    })
    .filter(Boolean)
    .map((otid) => t(vsOutcomeTypeKey(String(otid), false)))
    .join(', ');
}

export function getScheduleResultsConfig(sportId) {
  const id = sportId != null ? Number(sportId) : null;

  const getMarketColumns = (results) => {
    if (isBaseball(id)) return BASEBALL_MARKET_IDS;
    return getMarketTypeIdsFromResults(results);
  };

  const getRelevantMarketId = (marketColumns) => {
    if (isBaseball(id)) return BASEBALL_RELEVANT_MARKET_ID;
    if (isRacing(id)) return getRelevantMarketIdForRacing(marketColumns);
    return getRelevantMarketIdForGeneric(marketColumns);
  };

  const eventColumnLabelKey = isBaseball(id) ? 'BATTER' : 'EVENT';

  const renderEventCell = (row, { t }) => {
    if (isBaseball(id)) {
      return row.batter_name ?? `#${row.event_id ?? ''}`;
    }
    if (isRacing(id)) {
      return row.event_id != null
        ? `#${row.event_id}${row.description != null ? ` • ${row.description}` : ''}`
        : (row.description != null ? row.description : '—');
    }
    return row.description ?? `#${row.event_id ?? ''}`;
  };

  const renderMarketCell = (row, marketTypeId, options) => {
    const { t, sportId: sid, n_runners, oddsFormat, showOdds = true } = options || {};
    if (isRacing(id)) {
      const raw = row[String(marketTypeId)] ?? row[marketTypeId];
      return (
        <RacingOutcomeCell
          raw={raw}
          n_runners={n_runners ?? 6}
          sportId={sid ?? id}
          oddsFormat={oddsFormat ?? 'decimal'}
          marketTypeId={marketTypeId}
          showOdds={showOdds}
        />
      );
    }
    if (isBaseball(id)) {
      const raw = row[String(marketTypeId)] ?? row[marketTypeId];
      return (
        <BaseballOutcomeCell
          raw={raw}
          t={t}
          oddsFormat={oddsFormat ?? 'decimal'}
          showOdds={showOdds}
        />
      );
    }
    return getGenericOutcomeLabel(row, marketTypeId, t);
  };

  return {
    getMarketColumns,
    getRelevantMarketId,
    eventColumnLabelKey,
    renderEventCell,
    renderMarketCell,
    showRelevantMarketLabelInMobileSummary: !isBaseball(id) && !isRacing(id),
  };
}
